<script setup lang="ts">
  import { ref, computed } from "vue";
  import { Loader2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { useUploadButton } from "@utils/composables/uploadButton";

  interface UploadParams {
    file: File;
    fileType: string;
    index: number | null;
  }

  const props = defineProps<{
    type: string;
    index?: number | null;
    attachment?: string;
    handleUpload: (_params: UploadParams) => Promise<void>;
  }>();

  const { determineIcon, loading, startUpload, finishUpload, determineButtonInfo } = useUploadButton();
  const computedType = computed(() => `${props.type}/*`);
  const fileInputRef = ref<HTMLInputElement | null>(null);

  const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    startUpload();
    try {
      await props.handleUpload({ file, fileType: props.type, index: props.index ?? null });
      finishUpload();
    } catch (e) {
      finishUpload(e);
    }

    // Reset input so same file can be selected again
    if (fileInputRef.value) {
      fileInputRef.value.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.value?.click();
  };
</script>

<template>
  <div>
    <input
      ref="fileInputRef"
      type="file"
      :accept="computedType"
      class="hidden"
      @change="handleFileChange" />
    <Button
      type="button"
      variant="outline"
      :disabled="loading"
      class="w-full justify-start"
      @click="triggerFileInput">
      <Loader2
        v-if="loading"
        class="w-4 h-4 mr-2 animate-spin" />
      <component
        :is="determineIcon(attachment)"
        v-else-if="determineIcon(attachment)"
        class="w-4 h-4 mr-2" />
      <span class="truncate">{{ determineButtonInfo(attachment) }}</span>
    </Button>
  </div>
</template>
